import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Anchor,
  Box,
  Button,
  Container,
  CopyButton,
  Group,
  Paper,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { IconCheck, IconCopy, IconHelpCircle, IconLink, IconSearch } from "@tabler/icons-react";
import CheckAddressBackground from "../../checkBitcoinAddress/components/CheckAddressBackground";
import RizfulPitch from "../../rizfulPitch/RizfulPitch";
import ServerRenderedBlock, {
  hasServerRenderedBlock,
} from "../../helpers/ServerRenderedBlock";
import {
  TxLookupResult,
  fetchChainTip,
  lookupTx,
  parseTxInput,
} from "../helpers/txLookup";
import TxResult from "./TxResult";
import { EMPTY_WIZARD, WizardState, readWizardState, wizardParams } from "./SpeedUpWizard";
import WalletPicker, { Labelled } from "./WalletPicker";
import { Wallet, feeBumpData } from "../helpers/feeBumpWallets";
import { trackGoal } from "../../helpers/trackGoal";
import ToolLogo from "../../helpers/ToolLogo";

const PAGE_PATH = "/unconfirmed-bitcoin-transaction";
const PANEL_STYLE = {
  backgroundColor: "rgba(0, 0, 0, 0.35)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
};
const TIP_POLL_MS = 30_000;
// stop watching after this long; the visitor can always check again
const WATCH_LIMIT_MS = 6 * 3600_000;

// with the wizard's answers, so a shared link (or a recording) opens the same step
const shareUrl = (txid: string, wizard: WizardState = EMPTY_WIZARD) =>
  `${window.location.origin}${PAGE_PATH}?txid=${txid}${wizardParams(wizard)}`;

const WRONG_KIND: Record<string, React.ReactNode> = {
  address: (
    <>
      That looks like a Bitcoin address, not a transaction ID. To see an address's balance and its
      transactions, use the <Anchor href="/check_bitcoin_address" c="orange.4">Bitcoin Address Checker</Anchor>.
    </>
  ),
  invoice: (
    <>
      That's a Lightning invoice, not an on-chain transaction. Try the{" "}
      <Anchor href="/decode_invoice" c="orange.4">Lightning Invoice Decoder</Anchor>.
    </>
  ),
  lightning_address: (
    <>
      That's a Lightning Address. Try the{" "}
      <Anchor href="/lightning-address" c="orange.4">Lightning Address Tester</Anchor>.
    </>
  ),
  invalid: (
    <>
      Paste a transaction ID (txid): 64 characters of 0–9 and a–f. You'll find it in your wallet's
      transaction details. A link from a block explorer works too.
    </>
  ),
};

const NOT_FOUND_REASONS = [
  "It was replaced (RBF). A fee bump or a cancel gives the transaction a new ID; look for the new one in your wallet.",
  "It was never broadcast, or was broadcast only seconds ago. Wait a minute and check again.",
  "It was dropped: most nodes forget a transaction after 2 weeks unconfirmed, or sooner if its fee is too low. The coins are still in the sending wallet.",
  "It's a testnet or signet transaction. This checker only looks at Bitcoin mainnet.",
  "The ID is mistyped, or it's a block hash or an address rather than a transaction ID.",
];

const NotFound: React.FC = () => (
  <Paper p="md" radius="md" style={{ ...PANEL_STYLE, borderLeft: "4px solid var(--mantine-color-red-6)" }}>
    <Stack gap="xs">
      <Text c="white" fw={700} size="lg">
        Transaction not found
      </Text>
      <Text size="sm" c="dimmed">
        Neither the blockchain nor the mempool has this transaction. The usual reasons:
      </Text>
      <Stack gap={6}>
        {NOT_FOUND_REASONS.map((reason) => (
          <Text key={reason} size="sm" c="dimmed" pl="md" style={{ textIndent: "-0.9em" }}>
            • {reason}
          </Text>
        ))}
      </Stack>
    </Stack>
  </Paper>
);

const TxStatusContainer: React.FC = () => {
  const [input, setInput] = useState("");
  const [inputError, setInputError] = useState<React.ReactNode>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<TxLookupResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [loginHelps, setLoginHelps] = useState(false);
  const [lastBlockTime, setLastBlockTime] = useState<number | null>(null);
  const [watchStarted, setWatchStarted] = useState<number | null>(null);
  const [wizard, setWizard] = useState<WizardState>(() => readWizardState(window.location.search));
  const [txidHelpOpen, setTxidHelpOpen] = useState(false);
  const [txidHelpWallet, setTxidHelpWallet] = useState<Wallet | null>(null);
  const wizardRef = useRef(wizard);
  wizardRef.current = wizard;
  const runId = useRef(0);
  const originalTitle = useRef(document.title);

  const reset = () => {
    runId.current += 1;
    setResult(null);
    setError(null);
    setNotFound(false);
    setLoginHelps(false);
    setInputError(null);
    setWatchStarted(null);
    setWizard(EMPTY_WIZARD);
    document.title = originalTitle.current;
  };

  // keepWizard: the first check of a shared link keeps the wizard's answers from the URL
  const check = useCallback(async (raw: string, options: { silent?: boolean; keepWizard?: boolean } = {}) => {
    const parsed = parseTxInput(raw);
    if (parsed.kind !== "txid") {
      reset();
      setInputError(WRONG_KIND[parsed.kind]);
      return;
    }
    const run = options.silent ? runId.current : ++runId.current;
    if (!options.silent) {
      setLoading(true);
      setError(null);
      setNotFound(false);
      setLoginHelps(false);
      setInputError(null);
      if (!options.keepWizard) setWizard(EMPTY_WIZARD);
      window.history.replaceState(null, "", shareUrl(parsed.txid, options.keepWizard ? wizardRef.current : EMPTY_WIZARD));
    }
    const response = await lookupTx(parsed.txid);
    if (run !== runId.current) return;
    setLoading(false);
    if (!options.silent) {
      const outcome = response.result
        ? (response.result.confirmed ? "confirmed" : "unconfirmed")
        : response.notFound ? "not_found" : response.login ? "limited" : "error";
      trackGoal("Transaction checked", { result: outcome });
    }

    if (response.result) {
      setResult(response.result);
      setError(null);
      setNotFound(false);
      if (response.result.confirmed) {
        setWatchStarted(null);
        // a re-check found it mined: say so in the tab too
        if (options.silent) document.title = `✓ Confirmed · ${originalTitle.current}`;
      } else if (!options.silent) {
        setWatchStarted(Date.now());
      }
    } else if (!options.silent) {
      // a failed automatic re-check keeps the last good result
      setResult(null);
      setNotFound(response.notFound);
      setError(response.notFound ? null : response.error);
      setLoginHelps(response.login);
    } else if (response.login || response.error?.includes("limit")) {
      setWatchStarted(null);
    }
  }, []);

  // ?txid= share links
  useEffect(() => {
    const txid = new URLSearchParams(window.location.search).get("txid");
    if (txid) {
      setInput(txid);
      check(txid, { keepWizard: true });
    }
  }, [check]);

  // the wizard's answers go in the URL as they change
  useEffect(() => {
    if (result) window.history.replaceState(null, "", shareUrl(result.txid, wizard));
  }, [wizard, result?.txid]);

  // after a fee bump: check the replacement's new ID, which starts watching it
  const checkNewTxid = (txid: string) => {
    setInput(txid);
    check(txid);
    window.requestAnimationFrame(() => document.getElementById("tx-check-form")?.scrollIntoView({ block: "start" }));
  };

  // While unconfirmed: poll our own chain tip (no upstream calls), and re-check the
  // transaction only when a new block has been found.
  useEffect(() => {
    if (!result || result.confirmed || !watchStarted) return;
    let knownHeight = result.tip_height ?? 0;
    const timer = window.setInterval(async () => {
      if (document.hidden) return;
      if (Date.now() - watchStarted > WATCH_LIMIT_MS) {
        setWatchStarted(null);
        return;
      }
      const tip = await fetchChainTip();
      if (!tip) return;
      setLastBlockTime(tip.block_time);
      if (tip.height > knownHeight) {
        knownHeight = tip.height;
        check(result.txid, { silent: true });
      }
    }, TIP_POLL_MS);
    fetchChainTip().then((tip) => tip && setLastBlockTime(tip.block_time));
    return () => window.clearInterval(timer);
  }, [result, watchStarted, check]);

  return (
    <CheckAddressBackground>
      <Container size={760} px="md" py="xl">
        <Stack gap="lg">
          <ToolLogo />

          {hasServerRenderedBlock("ssr-tx-head") ? (
            <ServerRenderedBlock id="ssr-tx-head" />
          ) : (
            <Box ta="center">
              <Title order={1} c="white" mb="xs">
                Unconfirmed Bitcoin Transaction Checker
              </Title>
              <Text c="dimmed" size="sm">
                Is your transaction stuck? See where it is and how to speed it up.
              </Text>
            </Box>
          )}

          <Paper p="md" radius="md" style={PANEL_STYLE} id="tx-check-form">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                check(input);
              }}
            >
              <Stack gap="sm">
                <TextInput
                  aria-label="Transaction ID"
                  placeholder="Transaction ID (txid) or explorer link"
                  value={input}
                  onChange={(e) => {
                    setInput(e.currentTarget.value);
                    if (inputError) setInputError(null);
                  }}
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  size="md"
                  error={inputError}
                  styles={{
                    input: {
                      fontFamily: "monospace",
                      backgroundColor: "rgba(0, 0, 0, 0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.15)",
                      color: "white",
                    },
                  }}
                />
                <Group justify="space-between" gap="xs">
                  <Stack gap={2} style={{ flex: 1, minWidth: 180 }} align="flex-start">
                    <Text size="xs" c="dimmed">
                      Free. Bitcoin mainnet transactions only.
                    </Text>
                    {feeBumpData() && (
                      <Anchor
                        component="button"
                        type="button"
                        size="xs"
                        c="orange.4"
                        onClick={() => setTxidHelpOpen((open) => !open)}
                        aria-expanded={txidHelpOpen}
                      >
                        <Group gap={4} wrap="nowrap" component="span">
                          <IconHelpCircle size={14} />
                          Where do I find the transaction ID?
                        </Group>
                      </Anchor>
                    )}
                  </Stack>
                  <Group gap="xs">
                    <Button
                      variant="subtle"
                      color="gray"
                      onClick={() => {
                        setInput("");
                        reset();
                        window.history.replaceState(null, "", PAGE_PATH);
                      }}
                      disabled={!input}
                    >
                      Clear
                    </Button>
                    <Button
                      type="submit"
                      leftSection={<IconSearch size={16} />}
                      loading={loading}
                      disabled={!input.trim()}
                    >
                      Check transaction
                    </Button>
                  </Group>
                </Group>
              </Stack>
            </form>
          </Paper>

          {txidHelpOpen && feeBumpData() && (
            <Paper p="md" radius="md" style={PANEL_STYLE} data-wizard-step="find-txid">
              <Stack gap="sm">
                <Text c="white" fw={700} size="lg">
                  Which wallet or app sent or received it?
                </Text>
                {txidHelpWallet && (
                  <Paper p="sm" radius="md" style={{ backgroundColor: "rgba(251, 146, 60, 0.08)", border: "1px solid rgba(251, 146, 60, 0.45)" }}>
                    <Text size="sm" c="white" fw={700}>
                      {txidHelpWallet.name}
                    </Text>
                    <Text size="sm" c="dimmed">
                      <Labelled text={txidHelpWallet.txid} />
                    </Text>
                  </Paper>
                )}
                <WalletPicker data={feeBumpData()} selectedId={txidHelpWallet?.id} onPick={setTxidHelpWallet} />
                <Text size="xs" c="dimmed">
                  On a block explorer such as mempool.space, the transaction ID is the long code at the top of the transaction's
                  page. Copying the page's link works here too.
                </Text>
              </Stack>
            </Paper>
          )}

          {error && (
            <Alert color={loginHelps ? "orange" : "yellow"} variant="light">
              <Text size="sm">{error}</Text>
              {loginHelps && (
                <Group gap="xs" mt="sm">
                  <Button component="a" href="/login" size="xs" variant="outline" color="orange">
                    Log in
                  </Button>
                  <Button component="a" href="/create-account" size="xs" color="orange">
                    Create a free account
                  </Button>
                </Group>
              )}
            </Alert>
          )}

          {notFound && <NotFound />}

          {result && (
            <TxResult
              tx={result}
              watching={!!watchStarted && !result.confirmed}
              lastBlockTime={lastBlockTime}
              wizard={wizard}
              onWizardChange={setWizard}
              onCheckTxid={checkNewTxid}
              shareUrl={shareUrl(result.txid)}
            />
          )}

          {result && (
            <Paper p="md" radius="md" style={PANEL_STYLE}>
              <Group justify="space-between" align="center" gap="xs">
                <Group gap="xs">
                  <IconLink size={18} color="teal" />
                  <Text size="sm" c="dimmed">
                    Share this check:
                  </Text>
                </Group>
                <CopyButton value={shareUrl(result.txid, wizard)}>
                  {({ copied, copy }) => (
                    <Button
                      variant={copied ? "filled" : "outline"}
                      color={copied ? "teal" : "gray"}
                      size="xs"
                      leftSection={copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                      onClick={copy}
                    >
                      {copied ? "Link Copied!" : "Copy Link"}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </Paper>
          )}

          {hasServerRenderedBlock("ssr-tx-guide") && <ServerRenderedBlock id="ssr-tx-guide" />}

          <RizfulPitch lead="Tired of waiting for blocks? Payments to your Rizful Lightning Address settle in seconds." />
          <ServerRenderedBlock id="ssr-tool-footer" />
        </Stack>
      </Container>
    </CheckAddressBackground>
  );
};

export default TxStatusContainer;
